import { api } from '../../../core/utils/apiClient';
import { Company } from '../../dashboard_user/types';
import { CompanySource, CompanySourceLoadResult } from './types';

const savePartnerCompany: CompanySource['save'] = async (form, current) => {
  const payload = {
    name: form.name,
    domain: form.domain,
    description: form.description || undefined,
    website: form.website || undefined,
    founding_year: form.founding_year ? parseInt(form.founding_year) : undefined,
    employees: form.employees || undefined,
    industry: form.industry,
    location: form.location,
    sector_id: form.sector_id ?? undefined,
    linkedin_url: '',
    auto_enrich: true,
  };

  const response = current?.id
    ? await api.put(`/api/v1/companies/${current.id}`, payload)
    : await api.post('/api/v1/companies/', payload);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to save company');
  }

  return response.json();
};

export const partnerCompanySource: CompanySource = {
  async load(): Promise<CompanySourceLoadResult> {
    const response = await api.get('/api/v1/companies/my-companies');
    if (response.ok) {
      const data = await response.json();
      const companiesList = Array.isArray(data) ? data : data.companies || [];
      const partnerCompany = companiesList.length > 0 ? companiesList[0] : null;
      if (partnerCompany && typeof partnerCompany === 'object') {
        return { company: partnerCompany as Company };
      }
    }

    try {
      const initResponse = await api.post('/api/v1/companies/init');

      if (initResponse.status === 200) {
        const initData = await initResponse.json();
        const initializedCompany = (initData?.company || initData) as Company | null;
        if (initializedCompany) {
          return { company: initializedCompany };
        }
      } else if (initResponse.status === 204) {
        return { company: null, isFresh: true };
      }
    } catch (initError) {
      console.warn('Error initializing company:', initError);
    }

    return { company: null, isFresh: true };
  },

  save: savePartnerCompany,
};

export const makePartnerCompanySource = (companyId: number | null): CompanySource => ({
  async load(): Promise<CompanySourceLoadResult> {
    if (!companyId) {
      return partnerCompanySource.load();
    }

    const response = await api.get(`/api/v1/companies/${companyId}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Unable to load company');
    }

    return { company: (await response.json()) as Company };
  },

  save: savePartnerCompany,
});
